import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild } from '@angular/core'
import { Linqset, NODE_SUPERTYPE } from '../../common/linqset-model';
import { LinqModelToCanvasService } from '../../model-services/linqmodel.to.canvas.service';
import { LabelService } from '../../model-services/label.service';
import { GraphComponentComponent } from '../graph-component/graph-component.component';
import { LinqsetNodeVisualisation } from '../model/graph-model';
import { GraphService } from '../graph.service';
import { AdjacencyTypes, GraphComponent, GraphEditorInputMode, GraphItemTypes, IEdge, INode, ItemClickedEventArgs } from '@yfiles/yfiles';

export enum MiniGraphType {
    OVERVIEW="Overview",
    ADJACENT="Adjacent",
    CONTEXT="Context",
    DOWNSTREAM="Downstream",
    UPSTREAM="Upstream",
    HIDE="Hide graph"
}

@Component({
    selector: 'app-mini-graph-component',
    templateUrl: './mini-graph.component.html',
    styleUrls: ['./mini-graph.component.css'],
    standalone: false
})
export class MiniGraphComponent implements AfterViewInit, OnChanges, OnDestroy {
    @Input() mainGraphComponent:GraphComponentComponent
    @Input() graphType: MiniGraphType
    @Input() linqset:Linqset
    @Input() trigger:any

    @Output() itemSelectedEvent = new EventEmitter<LinqsetNodeVisualisation>();

    @ViewChild('miniGraphHost') miniGraphHost:ElementRef<HTMLDivElement>

    selectedNodes:INode[] = []
    selectedEdges:IEdge[] = []
    edges:IEdge[] = []

    miniGraphComponent!: GraphComponent

    constructor(private labelService: LabelService, private linqModelService: LinqModelToCanvasService, private graphService:GraphService) {
    }

    // the graph needs its element, which only exists once the view has been created
    ngAfterViewInit(): void {
        this.miniGraphComponent = new GraphComponent(this.miniGraphHost.nativeElement)
        this.setupGraph()
        this.refresh()
    }

    ngOnDestroy(): void {
        if (this.miniGraphComponent) this.miniGraphComponent.cleanUp()
    }

    ngOnChanges(changes: SimpleChanges) {
        // a new selection or a different view both redraw
        if (changes['trigger'] || changes['graphType'] || changes['linqset']) this.refresh()
    }

    private async refresh() {
        const selection = this.mainGraphComponent?.graphComponent?.selection
        this.selectedNodes = selection ? selection.nodes.toArray() : []
        this.selectedEdges = selection ? selection.edges.toArray() : []

        if (!this.miniGraphComponent) return
        if (!this.hasSelection() || !this.linqset) {
            this.edges = []
            this.miniGraphComponent.graph.clear()
            return
        }
        await this.populateGraph()
    }

    hasSelection() {
        return this.selectedNodes.length > 0 || this.selectedEdges.length > 0
    }

    // nothing connected in the chosen direction (a selected node is still shown on its own)
    isEmpty() {
        return this.hasSelection() && this.edges.length === 0
    }

    getEmptyMessage() {
        if (this.graphType === MiniGraphType.UPSTREAM) return "Nothing flows into the selection."
        if (this.graphType === MiniGraphType.DOWNSTREAM) return "Nothing flows on from the selection."
        return "The selection isn't connected to anything."
    }

    public async populateGraph() {
        this.miniGraphComponent.graph.clear()
        this.edges = []

        if (this.selectedNodes.length > 0) {
            switch (this.graphType) {
                case MiniGraphType.ADJACENT:
                    this.getAdjacent()
                    break
                case MiniGraphType.CONTEXT:
                    this.getContext()
                    break
                case MiniGraphType.UPSTREAM:
                    this.getUpDownstream(AdjacencyTypes.INCOMING)
                    break
                case MiniGraphType.DOWNSTREAM:
                    this.getUpDownstream(AdjacencyTypes.OUTGOING)
                    break
            }
        }else {
            // a selected connection shows its two ends
            this.edges = this.selectedEdges
        }

        // selected nodes are always shown, even when nothing is connected to them
        this.linqModelService.createMiniGraph(this.linqset, this.miniGraphComponent, this.edges, this.selectedNodes)
        this.labelService.updateLabelsForGraph(this.miniGraphComponent.graph, false, this.linqset, undefined)

        await this.graphService.runLayoutForLinqset(this.miniGraphComponent, this.linqset)
    }

    private setupGraph(): void {
        const inputMode = new GraphEditorInputMode({
            allowGroupingOperations: false,
            allowCreateNode: false,
            allowEditLabelOnDoubleClick: false,
            allowAddLabel: false,
            allowAdjustGroupNodeSize: false,
            allowCreateEdge:false,
            allowEditLabel: false,
            deletableItems: GraphItemTypes.NONE,
            ignoreVoidStyles: true
        })

        // clicking a node selects it in the main graph; connections have nothing to select there
        inputMode.addEventListener('item-left-clicked', (evt:ItemClickedEventArgs<INode>) => {
            if (evt.item instanceof INode && evt.item.tag?.node) this.itemSelectedEvent.emit(evt.item.tag)
        })

        this.miniGraphComponent.inputMode = inputMode

        this.graphService.initializeStyling(this.miniGraphComponent)
    }

    private get mainGraph() {
        return this.mainGraphComponent.graphComponent.graph
    }

    private addEdge(edge:IEdge) {
        if (!this.edges.includes(edge)) this.edges.push(edge)
    }

    private getAdjacent() {
        this.selectedNodes.forEach(n => this.mainGraph.edgesAt(n, AdjacencyTypes.ALL).forEach(e => this.addEdge(e)))
    }

    // the selection's neighbours and their neighbours
    private getContext() {
        this.selectedNodes.forEach(n => {
            this.mainGraph.edgesAt(n, AdjacencyTypes.ALL).forEach(e => {
                this.addEdge(e)
                const nextNode = e.sourceNode === n ? e.targetNode : e.sourceNode
                this.mainGraph.edgesAt(nextNode, AdjacencyTypes.ALL).forEach(ne => this.addEdge(ne))
            })
        })
    }

    private getUpDownstream(direction:AdjacencyTypes) {
        this.selectedNodes.forEach(n => this.getUpDownstreamForNode(n, direction))
    }

    // follows information and actions only, as roles and systems connect to much of the model
    private getUpDownstreamForNode(node:INode, direction:AdjacencyTypes) {
        this.mainGraph.edgesAt(node, direction)
            .filter(e => {
                const nextNode = direction === AdjacencyTypes.OUTGOING ? e.targetNode : e.sourceNode
                if (nextNode.tag?.node && nextNode.tag.node.superType !== NODE_SUPERTYPE.INFORMATION && nextNode.tag.node.superType !== NODE_SUPERTYPE.ACTION) return false
                return !this.edges.includes(e)
            })
            .forEach(e => {
                this.edges.push(e)
                const nextNode = direction === AdjacencyTypes.OUTGOING ? e.targetNode : e.sourceNode
                this.getUpDownstreamForNode(nextNode, direction)
            })
    }
}
